<template>
  <div v-if="video.status === 'completed' && video.videoUrl" class="subtitle-control" :class="{ compact }" @click.stop>
    <div v-if="!compact && hasCleanedVideo(video)" class="subtitle-version-tabs" aria-label="展示视频版本">
      <button type="button" :class="{ active: !isCleanedVideo(video) }" :aria-pressed="!isCleanedVideo(video)" :disabled="busy" @click="select('original')">原视频</button>
      <button type="button" :class="{ active: isCleanedVideo(video) }" :aria-pressed="isCleanedVideo(video)" :disabled="busy" @click="select('cleaned')">去字幕后</button>
    </div>
    <button
      v-if="!isCleanedVideo(video) && (canSubmit || hasCleanedVideo(video) || pending)"
      type="button" class="subtitle-action" :disabled="busy || pending" :title="label" :aria-label="label" @click="removeSubtitles"
    >
      <el-icon v-if="busy || pending" class="is-loading"><Loading /></el-icon>
      <svg v-else viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
        <rect x="3" y="4" width="18" height="15" rx="3" /><path d="M6 13h5m2 0h5M6 16h8M3 22 22 3" />
      </svg>
      <span v-if="!compact">{{ label }}</span>
    </button>
    <span v-if="!compact && video.subtitleRemoval?.status === 'failed'" class="subtitle-error" role="status">{{ video.subtitleRemoval.failReason || '去字幕失败，可重试' }}</span>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, onActivated, onDeactivated, onBeforeUnmount, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'
import { hasPermission } from '@/api/auth'
import { submitVideoSubtitle, selectVideoSubtitle, syncVideoSubtitle } from '@/api/videoSubtitle'
import { hasCleanedVideo, isCleanedVideo, subtitlePending, readSubtitleMetadata } from '../model/subtitleVersion'

const props = defineProps({ video: { type: Object, required: true }, compact: Boolean })
const emit = defineEmits(['change'])
const busy = ref(false)
const canSubmit = computed(() => hasPermission('subtitle_removal:submit'))
const pending = computed(() => subtitlePending(props.video.subtitleRemoval))
const label = computed(() => busy.value ? '处理中…' : pending.value ? '去字幕处理中…' : hasCleanedVideo(props.video) ? '展示去字幕视频' : props.video.subtitleRemoval?.status === 'failed' ? '重试去字幕' : '去字幕')
let disposed = false
let paused = false
let timer
let revision = 0
const update = (id, state) => {
  if (!disposed && String(props.video.id) === String(id)) emit('change', { id, subtitleRemoval: state })
}
const select = async version => {
  const id = props.video.id
  ++revision
  busy.value = true
  try {
    const response = await selectVideoSubtitle(id, version)
    update(id, response.data)
  } catch (error) {
    ElMessage.error(error.message || '切换视频失败')
  } finally { busy.value = false }
}
const removeSubtitles = async () => {
  if (busy.value || pending.value) return
  if (hasCleanedVideo(props.video)) return select('cleaned')
  if (!canSubmit.value) return
  const video = props.video
  busy.value = true
  try {
    const metadata = await readSubtitleMetadata(video.videoUrl)
    if (disposed || props.video.id !== video.id) return
    const response = await submitVideoSubtitle(video.id, {
      ...metadata, videoUrl: video.videoUrl, originalFilename: `video-${video.id}.mp4`, remark: video.remark || ''
    })
    update(video.id, response.data)
    ElMessage.success('去字幕任务已提交，完成后自动展示去字幕视频')
  } catch (error) {
    ElMessage.error(error.message || '提交去字幕失败')
  } finally { busy.value = false }
}
const schedule = () => {
  clearTimeout(timer)
  if (!disposed && !paused && pending.value) timer = setTimeout(poll, 10000)
}
const poll = async () => {
  const id = props.video.id
  const currentRevision = revision
  try {
    if (document.hidden || busy.value) return
    const response = await syncVideoSubtitle(id)
    if (revision === currentRevision) update(id, response.data)
  } catch (_) {
    // Keep the last state and retry on the next tick after temporary network failures.
  } finally { schedule() }
}
watch(() => [props.video.id, props.video.subtitleRemoval?.taskId, props.video.subtitleRemoval?.selectedVersion, pending.value], () => {
  ++revision
  schedule()
}, { immediate: true })
onBeforeUnmount(() => { disposed = true; clearTimeout(timer) })
onDeactivated(() => { paused = true; clearTimeout(timer) })
onActivated(() => { paused = false; schedule() })
</script>

<style scoped>
.subtitle-control { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.subtitle-action { display: inline-flex; align-items: center; justify-content: center; gap: 7px; min-height: 34px; padding: 7px 12px; color: #eee; background: #303030; border: 1px solid #555; border-radius: 10px; cursor: pointer; }
.subtitle-action:hover:not(:disabled) { background: #474747; border-color: #999; }
.subtitle-action:disabled { cursor: default; color: #bbb; }
.compact .subtitle-action { width: 34px; height: 34px; padding: 0; background: rgb(25 25 25 / 82%); border-radius: 50%; }
.subtitle-version-tabs { display: flex; padding: 3px; border: 1px solid #484848; border-radius: 10px; background: #222; }
.subtitle-version-tabs button { padding: 7px 14px; border: 0; border-radius: 7px; background: transparent; color: #aaa; cursor: pointer; }
.subtitle-version-tabs button.active { background: #505050; color: #fff; }
button:focus-visible { outline: 2px solid #71cdda; outline-offset: 3px; }
.subtitle-error { color: #f2aaaa; font-size: 12px; }
</style>
